import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { placeOrder } from "store/slices/main.slice";
import PayPal from "../payment/PayPal";
import Clover from "../payment/Clover";
import { ORDER } from "utils/constants";

type Props = {
  setStage: (v: string) => void;
};

const PAYMENT_FONT_FAMILY =
  "Manrope, Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, 'Apple Color Emoji', 'Segoe UI Emoji'";

const PaymentPage = ({ setStage }: Props) => {
  const dispatch = useAppDispatch();
  const { checkoutData, settings } = useAppSelector(({ main }) => main);
  const activeOrderType = checkoutData?.orderType || ORDER.TAKE_OUT;

  const enabledPayments = useMemo(() => {
    const suffix = activeOrderType === ORDER.DELIVERY ? "Delivery" : "Pickup";
    const allMethods = [
      {
        id: "pay_pal",
        key: `Paypal${suffix}` as const,
        label: "PayPal",
        description: "Secure PayPal checkout.",
        icon: <AccountBalanceWalletOutlinedIcon className="!text-[18px]" />,
        supported: true,
      },
      {
        id: "authorize",
        key: `Authorize${suffix}` as const,
        label: "Authorize.Net",
        description: "Pay with Authorize.Net.",
        icon: <CreditCardOutlinedIcon className="!text-[18px]" />,
        supported: false,
      },
      {
        id: "moneris",
        key: `Moneris${suffix}` as const,
        label: "Moneris",
        description: "Pay with Moneris.",
        icon: <CreditCardOutlinedIcon className="!text-[18px]" />,
        supported: false,
      },
      {
        id: "stripe",
        key: `Stripe${suffix}` as const,
        label: "Stripe",
        description: "Pay with Stripe.",
        icon: <CreditCardOutlinedIcon className="!text-[18px]" />,
        supported: false,
      },
      {
        id: "elavon",
        key: `Elavon${suffix}` as const,
        label: "Elavon",
        description: "Pay with Elavon.",
        icon: <CreditCardOutlinedIcon className="!text-[18px]" />,
        supported: false,
      },
      {
        id: "td",
        key: `TD${suffix}` as const,
        label: "TD",
        description: "Pay with TD.",
        icon: <CreditCardOutlinedIcon className="!text-[18px]" />,
        supported: false,
      },
      {
        id: "global",
        key: `Global${suffix}` as const,
        label: "Global",
        description: "Pay with Global Payments.",
        icon: <CreditCardOutlinedIcon className="!text-[18px]" />,
        supported: false,
      },
      {
        id: "clover",
        key: `Clover${suffix}` as const,
        label: "Clover",
        description: "Pay with Clover card checkout.",
        icon: <CreditCardOutlinedIcon className="!text-[18px]" />,
        supported: true,
      },
    ];

    return allMethods.filter((item) => Number((settings as any)?.[item.key]) === 1);
  }, [activeOrderType, settings]);

  const [paymentType, setPaymentType] = useState("");

  useEffect(() => {
    const requestedType = checkoutData?.paymentType || "";
    const preferredMatch = enabledPayments.find((item) => item.id === requestedType);
    setPaymentType(preferredMatch?.id || enabledPayments[0]?.id || "");
  }, [checkoutData?.paymentType, enabledPayments]);

  const selectedMethod = useMemo(
    () => enabledPayments.find((item) => item.id === paymentType),
    [enabledPayments, paymentType]
  );

  if (!checkoutData) return <></>;

  return (
    <div className="w-full h-full min-h-0 flex flex-col">
      <style>
        {`
          .paymentView {
            height: 100%;
            min-height: 0;
            display: flex;
            flex-direction: column;
            background: #f8fafc;
            font-family: ${PAYMENT_FONT_FAMILY};
            color: #101828;
          }
          .paymentHeader {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 10px 16px 12px;
            border-bottom: 1px solid rgba(15,23,42,.08);
            position: relative;
            overflow: hidden;
            backdrop-filter: blur(18px);
            background:
              radial-gradient(circle at top left, rgba(255,255,255,.92), transparent 34%),
              linear-gradient(135deg, rgba(255,255,255,.94), rgba(241,245,249,.84));
          }
          .paymentHeader::before {
            content: "";
            position: absolute;
            inset: -30% auto auto -10%;
            width: 240px;
            height: 240px;
            background: radial-gradient(circle, rgba(249,115,22,.14), transparent 64%);
            filter: blur(8px);
            animation: paymentHeaderFloat 9s ease-in-out infinite;
            pointer-events: none;
          }
          .paymentHeader::after {
            content: "";
            position: absolute;
            right: 8%;
            top: -70px;
            width: 180px;
            height: 180px;
            background: radial-gradient(circle, rgba(15,23,42,.08), transparent 68%);
            animation: paymentHeaderFloatAlt 11s ease-in-out infinite;
            pointer-events: none;
          }
          @keyframes paymentHeaderFloat {
            0%, 100% { transform: translate3d(0, 0, 0); }
            50% { transform: translate3d(26px, 10px, 0); }
          }
          @keyframes paymentHeaderFloatAlt {
            0%, 100% { transform: translate3d(0, 0, 0); }
            50% { transform: translate3d(-24px, 12px, 0); }
          }
          .paymentBody {
            flex: 1 1 auto;
            min-height: 0;
            overflow: auto;
            padding: 18px;
          }
          .paymentShell {
            display: grid;
            grid-template-columns: 220px minmax(0, 1fr);
            gap: 18px;
            min-height: 100%;
          }
          .paymentSidebar {
            border: 1px solid rgba(15,23,42,.08);
            border-radius: 20px;
            background: #fff;
            box-shadow: 0 12px 24px rgba(15,23,42,.05);
            padding: 10px;
            display: flex;
            flex-direction: column;
            gap: 8px;
            align-self: start;
          }
          .paymentNavButton {
            display: flex;
            align-items: center;
            gap: 10px;
            width: 100%;
            border: 1px solid rgba(15,23,42,.08);
            border-radius: 14px;
            background: rgba(248,250,252,.96);
            padding: 10px 12px;
            text-align: left;
            transition: .2s ease;
          }
          .paymentNavButtonActive {
            background: linear-gradient(180deg, rgba(17,24,39,.97), rgba(31,41,55,.97));
            color: #fff;
            border-color: rgba(17,24,39,.9);
            box-shadow: 0 14px 24px rgba(15,23,42,.18);
          }
          .paymentNavTitle {
            font-size: 14px;
            font-weight: 800;
            line-height: 1.2;
          }
          .paymentNavText {
            font-size: 11px;
            line-height: 1.35;
            opacity: .8;
            margin-top: 3px;
          }
          .paymentPanel {
            border: 1px solid rgba(15,23,42,.08);
            border-radius: 22px;
            background: #fff;
            box-shadow: 0 12px 24px rgba(15,23,42,.06);
            padding: 18px;
          }
          .paymentPanelTag {
            display: inline-flex;
            align-items: center;
            border-radius: 999px;
            background: rgba(15,23,42,.05);
            padding: 6px 10px;
            font-size: 12px;
            font-weight: 700;
            color: #475467;
          }
          .paymentEmbed {
            margin-top: 16px;
            border-radius: 18px;
            background: #fff;
          }
          .paymentPlaceholder {
            margin-top: 16px;
            border: 1px dashed rgba(15,23,42,.14);
            border-radius: 18px;
            background: rgba(248,250,252,.75);
            padding: 20px;
          }
          @media (max-width: 768px) {
            .paymentBody {
              padding: 14px;
            }
            .paymentShell {
              grid-template-columns: 1fr;
              gap: 12px;
            }
            .paymentSidebar {
              display: grid;
              grid-template-columns: repeat(2, minmax(0, 1fr));
              gap: 8px;
            }
          }
          @media (max-width: 520px) {
            .paymentSidebar {
              grid-template-columns: 1fr;
            }
            .paymentPanel {
              padding: 14px;
            }
            .paymentNavButton {
              padding: 9px 10px;
            }
          }
        `}
      </style>

      <div className="paymentHeader">
        <div className="relative z-[1]" onClick={() => setStage("DETAILS")}>
          <ArrowBackIosNewIcon className="!text-[15px] !text-primary cursor-pointer" />
        </div>
        <h4 className="relative z-[1] text-[28px] leading-none font-[800] tracking-[-0.02em] text-[#101828] text-center max-sm:text-[22px]">
          Pay for Your Order
        </h4>
        <div />
      </div>

      <div className="paymentView">
        <div className="paymentBody">
          <div className="paymentShell">
            <div className="paymentSidebar">
              {enabledPayments.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`paymentNavButton ${
                    paymentType === item.id ? "paymentNavButtonActive" : ""
                  }`}
                  onClick={() => setPaymentType(item.id)}
                >
                  <div>{item.icon}</div>
                  <div>
                    <div className="paymentNavTitle">{item.label}</div>
                    <div className="paymentNavText">{item.description}</div>
                  </div>
                </button>
              ))}
            </div>

            <div className="paymentPanel">
              {selectedMethod ? (
                <>
                  <div className="paymentPanelTag">Selected payment: {selectedMethod.label}</div>
                  <div className="mt-4">
                    <h3 className="text-[24px] font-[800] leading-tight text-[#101828]">
                      {selectedMethod.label}
                    </h3>
                    <p className="mt-2 text-[14px] text-[#475467]">
                      {selectedMethod.description}
                    </p>
                  </div>

                  {paymentType === "pay_pal" && (
                    <div className="paymentEmbed">
                      <PayPal
                        value={checkoutData.total.toFixed(2)}
                        onSubmit={() =>
                          dispatch(placeOrder({ paymentType: "pay_pal", setStage }))
                        }
                      />
                    </div>
                  )}

                  {paymentType === "clover" && (
                    <div className="paymentEmbed">
                      <Clover
                        value={checkoutData.total}
                        onSubmit={() =>
                          dispatch(placeOrder({ paymentType: "clover", setStage }))
                        }
                      />
                    </div>
                  )}

                  {selectedMethod && !selectedMethod.supported && (
                    <div className="paymentPlaceholder">
                      <p className="text-[15px] font-[800] text-[#101828]">
                        {selectedMethod.label} is enabled in settings
                      </p>
                      <p className="mt-2 text-[14px] leading-6 text-[#475467]">
                        This payment option is available for{" "}
                        {activeOrderType === ORDER.DELIVERY ? "delivery" : "pickup"} in your
                        store settings, but this screen currently has a live payment component
                        only for PayPal and Clover.
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="paymentPlaceholder">
                  <p className="text-[15px] font-[800] text-[#101828]">
                    No online payment method is enabled
                  </p>
                  <p className="mt-2 text-[14px] leading-6 text-[#475467]">
                    Turn on at least one payment gateway for this order type in admin settings.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
